// Copyright (c) 2026, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuthContext } from '@asgardeo/auth-react';
import { AppConfig } from '@src/config/config';

const BANNER_BG = '#ff7300';
const ROW_FONT_SIZE = 11;

type KVRow = { key: string; value: string };
type RawRow = { raw: string };
type Row = KVRow | RawRow;

export const parseResponse = (raw: string): { banner: string | null; rows: Row[] } => {
  const parts = raw
    .split('|')
    .map((p) => p.trim())
    .filter(Boolean);

  const bannerIndex = parts.findIndex((p) => !p.includes(':'));
  const banner = bannerIndex !== -1 ? parts[bannerIndex] : null;

  const rows: Row[] = parts
    .filter((_, i) => i !== bannerIndex)
    .map((p) => {
      const colonIdx = p.indexOf(':');
      if (colonIdx === -1) return { raw: p };
      return { key: p.slice(0, colonIdx).trim(), value: p.slice(colonIdx + 1).trim() };
    });

  return { banner, rows };
};

const AssetView = () => {
  const theme = useTheme();
  const { getAccessToken } = useAuthContext();
  const [assetId, setAssetId] = useState('');
  const [response, setResponse] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const rowEvenBg = theme.palette.mode === 'dark' ? theme.palette.action.hover : '#f9f9f9';
  const rowOddBg = theme.palette.background.paper;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setAssetId(params.get('assetId') ?? '');
  }, []);

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError(false);
      setSearched(true);
      // Double-encode to handle special characters through the CloudFlare rule
      const encodedAssetId = encodeURIComponent(encodeURIComponent(assetId));
      const token = await getAccessToken();
      const res = await fetch(`${AppConfig.BACKEND_URL}${encodedAssetId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setResponse(typeof data.value === 'string' ? data.value : '');
      setError(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(true);
      setResponse('');
    } finally {
      setLoading(false);
    }
  };

  const { banner, rows } = response ? parseResponse(response) : { banner: null, rows: [] };
  const hasResults = banner !== null || rows.length > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <TextField
          label="Asset ID"
          id="assetId"
          value={assetId}
          onChange={(e) => setAssetId(e.target.value)}
          size="small"
          fullWidth
        />
        <Button variant="contained" onClick={handleSearch} disabled={loading} sx={{ whiteSpace: 'nowrap' }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error fetching data.
        </Alert>
      )}

      {searched && !loading && !error && !hasResults && (
        <Alert severity="info">No results found for the given Asset ID.</Alert>
      )}

      {hasResults && (
        <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {banner && (
            <Box sx={{ bgcolor: 'primary.dark', px: 2, py: 1.25, display: 'flex', alignItems: 'center' }}>
              <Chip
                label={banner}
                size="small"
                sx={{
                  bgcolor: BANNER_BG,
                  color: '#fff',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  fontSize: ROW_FONT_SIZE,
                }}
              />
            </Box>
          )}
          {rows.map((row, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                px: 2,
                py: 1,
                bgcolor: i % 2 === 0 ? rowEvenBg : rowOddBg,
              }}
            >
              {'key' in row ? (
                <>
                  <Typography sx={{ width: '40%', color: 'text.secondary', fontSize: ROW_FONT_SIZE }}>
                    {row.key}
                  </Typography>
                  <Typography sx={{ width: '60%', color: 'text.primary', fontSize: ROW_FONT_SIZE, fontWeight: 500 }}>
                    {row.value}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ width: '100%', color: 'text.secondary', fontSize: ROW_FONT_SIZE, fontStyle: 'italic' }}>
                  {row.raw}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default AssetView;
